import { useApiGen } from "$root/api/hooks";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import {
	EditPortfolioMultiV4ContextProvider,
	EditPortfolioMultiV4InContext,
} from "$root/components/Portfolio/CreatePortfolio/EditPortfolioMulti";
import { makeUnsavedDataHandler } from "$root/components/Portfolio/CreatePortfolio/shared";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { InvestmentBulkBulkStaticConfigurationControllerV1ApiFactory } from "@eu.mdotm.api/sphere";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { ActionText, AutoTooltip, Icon, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePortfolioStudioRedirect } from "../PortfoliosStudio/usePortfolioStudioRedirect";

/** New/Edit merged together */
export default function EditPortfolioMulti({
	workspaceUid,
	bulkUid,
}: {
	workspaceUid?: string;
	bulkUid: string;
}): React.JSX.Element {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { t } = useTranslation();
	const investmentBulkBulkStaticV1Api = useApiGen(InvestmentBulkBulkStaticConfigurationControllerV1ApiFactory);

	const portfoliosQuery = useQueryNoRefetch({
		queryKey: ["portfolioNames", bulkUid],
		queryFn: async () => {
			const stepAvailability = await axiosExtract(
				investmentBulkBulkStaticV1Api.getEnhancementConfigurationStepsAvailability2(bulkUid),
			);
			return stepAvailability;
		},
	});
	async function Submit(uuid: string) {
		try {
			setIsSubmitting(true);
			await investmentBulkBulkStaticV1Api.runEnhancementConfiguration2(uuid);
			setIsSubmitting(false);
			platformToast({
				children: "Sphere has taken over your request",
				severity: "success",
				icon: "Portfolio",
			});

			portfolioStudioRedirect.navigate();
		} catch (error) {
			platformToast({
				children: t("PORTFOLIOS.PORTFOLIO_GENERIC_UPLOAD_ERROR"),
				severity: "error",
				icon: "Portfolio",
			});
			console.error(error);
		}
	}

	const crumbs = useCrumbs();
	const workspacePath = useCrumbs().useWorkspacePath(workspaceUid);
	const portfolioStudioRedirect = usePortfolioStudioRedirect(workspaceUid);

	return (
		<ReactQueryWrapperBase query={portfoliosQuery}>
			{({ investments }) => (
				<EditPortfolioMultiV4ContextProvider area={{ name: "edit-bulk-settings", bulkUid, edit: true, editable: true }}>
					{({ stepsMetadata, currentStep, setStepsMetadata, area, submittersRef }) => (
						<>
							<PageHeader
								crumbs={[
									...crumbs.portfolioStudio("Portfolios"),
									...workspacePath,
									{
										label: "Settings",
									},
								]}
								titleAction={
									<div className="flex items-center space-x-2">
										<Button size="small" onClick={portfolioStudioRedirect.navigate} palette="tertiary">
											Cancel
										</Button>
										<Button
											size="small"
											onClickAsync={async () => {
												try {
													const saveHandler = makeUnsavedDataHandler({
														currentStep,
														setStepsMetadata,
														submittersRef,
													});
													const shouldContinue = (await saveHandler()) === "continue";
													if (!shouldContinue) {
														throw new Error("unable to proceed, some step may require additional data or corrections");
													}
													await Submit(area.bulkUid ?? "");
												} catch (error) {
													platformToast({
														children: `Unable to proceed, ${t(
															`PORTFOLIO_WIZARD.STEP_NAME.${currentStep}`,
														)} step requires additional data or correction`,
														severity: "info",
														icon: "Portfolio",
													});
													throw error;
												}
											}}
											palette="primary"
										>
											<Icon icon="save" size="1.2em" />
											&nbsp;Save settings
										</Button>
									</div>
								}
								subTitle={
									<div className="flex justify-between items-center py-2.5">
										<Text>
											{investments?.length === 2 ? (
												<>
													You are editing the settings for:{" "}
													<ActionText
														href={typedUrlForRoute("PortfolioDetails", { portfolioUid: investments?.[0].uuid ?? "" })}
													>
														{investments[0].name ?? "-"}
													</ActionText>{" "}
													and{" "}
													<ActionText
														href={typedUrlForRoute("PortfolioDetails", { portfolioUid: investments?.[1].uuid ?? "" })}
													>
														{investments[1].name ?? "-"}
													</ActionText>
												</>
											) : (
												<>
													You are editing the settings for:{" "}
													<ActionText
														href={typedUrlForRoute("PortfolioDetails", { portfolioUid: investments?.[0].uuid ?? "" })}
													>
														{investments?.[0].name ?? "-"}
													</ActionText>{" "}
													and{" "}
													<AutoTooltip
														overrideColor={themeCSSVars.palette_N200}
														trigger={({ innerRef }) => (
															<ActionText onClick={noop} innerRef={innerRef}>
																{(investments?.length ?? 0) - 1} more
															</ActionText>
														)}
													>
														<TooltipContent>
															<ul className="list-disc pl-6">
																{(investments?.slice(1) ?? []).map((p, i) => (
																	<li key={p.uuid ?? i}>{p.name ?? "-"}</li>
																))}
															</ul>
														</TooltipContent>
													</AutoTooltip>
												</>
											)}
										</Text>
									</div>
								}
							/>
							<EditPortfolioMultiV4InContext />

							<LeavePrompt
								when={stepsMetadata[currentStep].dirty || !isSubmitting}
								title={t("LEAVE_PAGE")}
								pathToNotBlock={[
									"/login",
									`${typedUrlForRoute("Portfolios/SettingsPortfolioMulti", { bulkUid })}#${currentStep}`,
									typedUrlForRoute("Portfolios/SettingsPortfolioMulti", { bulkUid }),
								]}
							>
								{t("PORTFOLIOS.CREATE_LEAVE")}
							</LeavePrompt>
						</>
					)}
				</EditPortfolioMultiV4ContextProvider>
			)}
		</ReactQueryWrapperBase>
	);
}
